import { useRef, useState } from "react";
import { api } from "../api";
import { ErrorNote } from "../components/Feedback";
import { md5OfFile, uploadErrorText } from "../components/UploadPanel";
import { Btn } from "../components/Shared";
import { SuccessNote } from "./admin/parts";

// "Import RO-Crate" on the study page (whoever edits the study: its author, the project's curators): a dataset or study crate
// zip, as "Download RO-Crate" writes it, becomes datasets of this study with their files,
// column dictionaries (backend/unio/routers/crate_import.py). A protocol is a file of its
// dataset (role protocol); the structured protocols of an older crate are listed as not imported.
//
//   POST /studies/{id}/import/ro-crate[?create_fields=true], raw body
//   -> {kind, datasets: [{id, name, file_count}], missing: [{name, reason}],
//       skipped: [{name, reason}], warnings: [str], fields_created: [str]}
// The crate's field values fill the project's fields by key. A curator of the project
// (`study.can_manage_project`) may tick "Create the fields this project lacks" (task F): a key
// the project has no field for becomes one of its fields first (listed as "Fields created").

function Listed({ label, items, render }) {
  if (!items.length) return null;
  return (
    <div style={{ marginBottom: 8 }}>
      <h4 style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 700 }}>{label}</h4>
      <ul aria-label={label} style={{ margin: 0, paddingLeft: 20 }}>
        {items.map((x, i) => <li key={i}>{render(x)}</li>)}
      </ul>
    </div>
  );
}

function ImportResult({ result }) {
  const n = result.datasets.length;
  const files = result.datasets.reduce((sum, d) => sum + d.file_count, 0);
  return (
    <section aria-label="RO-Crate import result" style={{ marginTop: 12, fontSize: 14 }}>
      <SuccessNote>
        Imported {n} dataset{n === 1 ? "" : "s"} with {files} file{files === 1 ? "" : "s"}
        {result.missing.length > 0 && `; ${result.missing.length} file${result.missing.length === 1 ? " was" : "s were"} not in the archive`}.
      </SuccessNote>
      <Listed label="Fields created" items={result.fields_created || []} render={f => f} />
      <Listed label="Missing files" items={result.missing} render={m => `${m.name}: ${m.reason}`} />
      <Listed label="Warnings" items={result.warnings} render={w => w} />
      <Listed label="Not imported" items={result.skipped} render={s => `${s.name}: ${s.reason}`} />
    </section>
  );
}

// The curator's consent for an import to create the project fields the crate needs (task F).
export function CreateFieldsBox({ checked, onChange, disabled }) {
  return (
    <label style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 13, color: "#374151" }}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={e => onChange(e.target.checked)} />
      Create the fields this project lacks
    </label>
  );
}

export default function StudyCrateImport({ study, onImported }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [createFields, setCreateFields] = useState(false);
  const input = useRef(null);

  const choose = async e => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setError(null);
    setResult(null);
    if (file.size === 0) {
      setError(new Error(`${file.name} is empty (0 bytes).`));
      return;
    }
    setBusy(true);
    try {
      const md5 = await md5OfFile(file);
      const q = createFields ? "?create_fields=true" : "";
      setResult(await api.upload(`/studies/${study.id}/import/ro-crate${q}`, file, { md5 }));
      onImported();
    } catch (err) {
      setError(new Error(uploadErrorText(err)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <span style={{ display: "inline-flex", flexDirection: "column", gap: 6 }}>
      {/* Not a tab stop of its own: the button below opens it (as UploadPanel's). Hidden with
          display: none, not a 1 px box, which the phone rules would grow into a 44 px row. */}
      <input ref={input} id={`crate-import-${study.id}`} type="file" accept=".zip,application/zip"
             aria-label="Import RO-Crate" style={{ display: "none" }} onChange={choose} disabled={busy} />
      <Btn variant="secondary" onClick={() => input.current && input.current.click()} disabled={busy}>
        {busy ? "Importing…" : "Import RO-Crate"}
      </Btn>
      {study.can_manage_project && <CreateFieldsBox checked={createFields} onChange={setCreateFields} disabled={busy} />}
      <ErrorNote error={error} />
      {result && <ImportResult result={result} />}
    </span>
  );
}
